/**
 * Database Synchronization
 * Handles synchronization between IndexedDB and backend API with offline-first approach
 * @module dbSync
 */

import store from '../store'
import {
  STORES,
  addItem,
  getItemsByIndex,
  updateItem,
  deleteItem,
  getItemById,
  getAllItems,
  getStoreKeyPath
} from './indexedDB'

/**
 * @typedef {Object} SyncOperation
 * @property {string} type - The type of operation ('get', 'create', 'update', 'delete')
 * @property {string} storeName - The name of the store
 * @property {Object} data - The data for the operation
 * @property {Object} [requestConfig] - Additional request configuration
 */

/**
 * @typedef {Object} RequestConfig
 * @property {string} [method] - HTTP method to use
 * @property {Object} [headers] - Request headers
 * @property {Object} [fetchOptions] - Additional fetch options
 */

// Sync status constants
export const SYNC_STATUS = {
  SYNCED: 'synced',
  PENDING: 'pending',
  FAILED: 'failed'
}

// API endpoints
const API_ENDPOINTS = {
  CONNECTOR_EXECUTE: `${process.env.REACT_APP_AGILITE_API_URL}/connectors/execute`,
  KEYWORDS: `${process.env.REACT_APP_AGILITE_API_URL}/keywords/getValuesByProfileKey`
}

/**
 * Adds an operation to the sync queue
 * @param {SyncOperation} operation - The operation to sync
 * @returns {Promise<number>} The ID of the added operation
 * @private
 */
const addToSyncQueue = async (operation) => {
  const syncOperation = {
    ...operation,
    createdAt: new Date().toISOString(),
    retryCount: 0,
    status: SYNC_STATUS.PENDING
  }

  return await addItem(STORES.SYNC_QUEUE, syncOperation)
}

/**
 * Validates an API response
 * @param {Object} response - The response from the API
 * @param {string} operationType - The type of operation
 * @param {boolean} [isConnectorExecute=false] - Whether this is a connector execute API request
 * @returns {boolean} Whether the response is valid
 * @private
 */
const validateApiResponse = (response, operationType, isConnectorExecute = false) => {
  if (!response) {
    return false
  }

  if (isConnectorExecute) {
    return typeof response === 'object'
  }

  if (operationType === 'create' || operationType === 'update') {
    return !!response.id
  }

  return true
}

/**
 * Syncs a single operation with the server
 * @param {SyncOperation} operation - The operation to sync
 * @returns {Promise<Object>} The response from the server
 * @private
 */
const syncOperation = async (operation) => {
  const { type, storeName, data, requestConfig = {} } = operation

  // Check for connector execute API first
  const isConnectorExecute =
    (data?.requestConfig?.headers?.['profile-key'] && data?.requestConfig?.headers?.['route-key']) ||
    (requestConfig?.headers?.['profile-key'] && requestConfig?.headers?.['route-key'])

  // Get the API endpoint
  const endpoint = isConnectorExecute ? API_ENDPOINTS.CONNECTOR_EXECUTE : API_ENDPOINTS[storeName.toUpperCase()]
  if (!endpoint && !isConnectorExecute) {
    throw new Error(`No API endpoint defined for store: ${storeName}`)
  }

  // Prepare request parameters
  const headers = {
    'Content-Type': 'application/json',
    ...(isConnectorExecute ? { 'api-key': process.env.REACT_APP_AGILITE_API_KEY } : {}),
    ...(data?.requestConfig?.headers || requestConfig?.headers || {})
  }

  // For connector execute API, always use POST method
  const method = isConnectorExecute
    ? 'POST'
    : requestConfig.method ||
      {
        get: 'GET',
        create: 'POST',
        update: 'PUT',
        delete: 'DELETE'
      }[type] ||
      'GET'

  // Construct URL
  let url = endpoint
  if (!isConnectorExecute && data.id) {
    url = `${endpoint}/${data.id}`
  } else if (!isConnectorExecute && type === 'get' && data.params) {
    const queryParams = new URLSearchParams(data.params).toString()
    url = `${endpoint}${queryParams ? `?${queryParams}` : ''}`
  }

  // Prepare request body
  let body
  if (isConnectorExecute && data.body) {
    body = JSON.stringify(
      typeof data.body.data === 'string'
        ? data.body
        : {
            ...data.body,
            data: JSON.stringify(data.body.data)
          }
    )
  } else if (type === 'get' && method !== 'GET' && data.body) {
    body = JSON.stringify(data.body)
  } else if (type !== 'get') {
    body = JSON.stringify(data.body)
  }

  // Make the API request
  const response = await fetch(url, {
    method,
    headers,
    body,
    ...(data?.requestConfig?.fetchOptions || requestConfig?.fetchOptions || {})
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`API error (${response.status}): ${errorText}`)
  }

  // Handle response
  if (method === 'DELETE') {
    return { id: data.id }
  }

  const responseData = await response.json()
  if (type !== 'get') {
    validateApiResponse(responseData, type, isConnectorExecute)
  }

  return responseData
}

/**
 * Processes the sync queue
 * @returns {Promise<boolean>} Success status
 * @private
 */
export const processSyncQueue = async () => {
  if (processSyncQueue.isRunning) {
    return false
  }

  try {
    processSyncQueue.isRunning = true

    // Get pending operations
    let pendingOperations = []
    try {
      pendingOperations = await getItemsByIndex(STORES.SYNC_QUEUE, 'status', SYNC_STATUS.PENDING)
    } catch {
      const allOperations = await getAllItems(STORES.SYNC_QUEUE)
      pendingOperations = allOperations.filter((op) => op.status === SYNC_STATUS.PENDING)
    }

    if (pendingOperations.length === 0) {
      return true
    }

    // Sort operations by creation date
    pendingOperations.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

    // Process each operation
    for (const operation of pendingOperations) {
      try {
        await syncOperation(operation)

        // Update operation status
        await updateItem(STORES.SYNC_QUEUE, {
          ...operation,
          status: SYNC_STATUS.SYNCED,
          syncedAt: new Date().toISOString()
        })

        // Update item status in its store
        if (operation.data && operation.storeName) {
          try {
            const item = await getItemById(operation.storeName, operation.data[getStoreKeyPath(operation.storeName)])
            if (item) {
              await updateItem(operation.storeName, {
                ...item,
                syncStatus: SYNC_STATUS.SYNCED,
                syncedAt: new Date().toISOString()
              })
            }
          } catch {
            // Ignore errors updating store status
          }
        }
      } catch (error) {
        const retryCount = operation.retryCount + 1
        const maxRetries = 5

        await updateItem(STORES.SYNC_QUEUE, {
          ...operation,
          retryCount,
          status: retryCount >= maxRetries ? SYNC_STATUS.FAILED : SYNC_STATUS.PENDING,
          lastError: error.message,
          lastErrorAt: new Date().toISOString()
        })
      }
    }

    return true
  } catch {
    return false
  } finally {
    processSyncQueue.isRunning = false
  }
}

// Initialize the running flag
processSyncQueue.isRunning = false

/**
 * Gets data with sync support
 * @param {string} storeName - The name of the store
 * @param {Object} options - The options for the get operation
 * @param {string|number} [options.id] - The ID of a specific item to get
 * @param {Object} [options.params] - Query parameters for filtering
 * @param {Object} [options.body] - Request body
 * @param {RequestConfig} [options.requestConfig] - Additional request configuration
 * @returns {Promise<Object|Array>} The fetched data
 */
export const getDataWithSync = async (storeName, options = {}) => {
  const isOnline = store.getState().core.online
  const { id, key, params, body, requestConfig = {} } = options
  const keyPath = getStoreKeyPath(storeName)
  const isConnectorExecute = requestConfig.headers?.['profile-key'] && requestConfig.headers?.['route-key']

  // Check local data first
  let localData = id ? await getItemById(storeName, id) : null

  if (isOnline) {
    try {
      // Prepare request body for connector execute API
      let requestBody = body
      if (isConnectorExecute && body?.filter && typeof body.filter !== 'string') {
        requestBody = {
          ...body,
          filter: JSON.stringify(body.filter)
        }
      }

      const operation = {
        type: 'get',
        storeName,
        data: { id, params, body: requestBody },
        requestConfig
      }

      const responseData = await syncOperation(operation)

      // Update local data
      if (id && responseData && (responseData[keyPath] || responseData.id)) {
        await updateItem(storeName, {
          ...responseData,
          syncStatus: SYNC_STATUS.SYNCED
        })
      } else if (Array.isArray(responseData)) {
        if (key) {
          const existingItem = await getItemById(storeName, key)
          await (existingItem ? updateItem : addItem)(storeName, {
            ...(existingItem || responseData),
            key,
            syncStatus: SYNC_STATUS.SYNCED
          })
        } else {
          await Promise.all(
            responseData
              .filter((item) => item && (item[keyPath] || item.id))
              .map(async (item) => {
                const itemId = item[keyPath] || item.id
                const existingItem = await getItemById(storeName, itemId)
                return (existingItem ? updateItem : addItem)(storeName, {
                  ...item,
                  syncStatus: SYNC_STATUS.SYNCED
                })
              })
          )
        }
      }

      return responseData
    } catch (error) {
      if (id && localData) return localData
      if (!id) return await getAllItems(storeName)
      throw error
    }
  }

  // Offline mode
  if (id) {
    if (!localData) {
      throw new Error(`Item with ID ${id} not found in ${storeName} while offline`)
    }
    return localData
  }

  return await getAllItems(storeName)
}

/**
 * Creates an item with sync support
 * @param {string} storeName - The name of the store
 * @param {Object} item - The item to create
 * @returns {Promise<Object>} The created item
 */
export const createItemWithSync = async (storeName, item) => {
  const isOnline = store.getState().core.online
  const keyPath = getStoreKeyPath(storeName)
  const isConnectorExecute = item.requestConfig?.headers?.['profile-key'] && item.requestConfig?.headers?.['route-key']

  // Parse data for storage
  let dataToStore = item
  if (isConnectorExecute && item.body?.data) {
    try {
      const parsedData = typeof item.body.data === 'string' ? JSON.parse(item.body.data) : item.body.data
      dataToStore = {
        ...parsedData,
        syncStatus: SYNC_STATUS.PENDING
      }
    } catch {
      dataToStore = {
        ...item.body.data,
        syncStatus: SYNC_STATUS.PENDING
      }
    }
  } else {
    dataToStore = {
      ...item,
      syncStatus: SYNC_STATUS.PENDING
    }
  }

  // Store locally
  const id = await addItem(storeName, dataToStore)
  const createdItem = { ...dataToStore, [keyPath]: id }

  // Prepare sync data
  let syncData = item
  if (isConnectorExecute && item.body?.data && typeof item.body.data !== 'string') {
    syncData = {
      ...item,
      body: {
        ...item.body,
        data: JSON.stringify(item.body.data)
      }
    }
  }

  // Add to sync queue
  await addToSyncQueue({
    type: 'create',
    storeName,
    data: syncData
  })

  // Try immediate sync if online
  if (isOnline) {
    processSyncQueue().catch(() => {})
  }

  return createdItem
}

/**
 * Updates an item with sync support
 * @param {string} storeName - The name of the store
 * @param {Object} item - The item to update
 * @returns {Promise<Object>} The updated item
 */
export const updateItemWithSync = async (storeName, item) => {
  const isOnline = store.getState().core.online
  const keyPath = getStoreKeyPath(storeName)
  const isConnectorExecute = item.requestConfig?.headers?.['profile-key'] && item.requestConfig?.headers?.['route-key']

  // Parse data for storage
  let dataToStore = item
  if (isConnectorExecute && item.body?.data) {
    try {
      const parsedData = typeof item.body.data === 'string' ? JSON.parse(item.body.data) : item.body.data
      const existingItem = await getItemById(storeName, item[keyPath] || item.body?.recordId)
      dataToStore = {
        ...existingItem,
        ...parsedData,
        syncStatus: SYNC_STATUS.PENDING,
        [keyPath]: item[keyPath] || item.body?.recordId
      }
    } catch {
      dataToStore = {
        ...item.body.data,
        syncStatus: SYNC_STATUS.PENDING,
        [keyPath]: item[keyPath] || item.body?.recordId
      }
    }
  } else {
    dataToStore = {
      ...item,
      syncStatus: SYNC_STATUS.PENDING
    }
  }

  // Update locally
  const updatedItem = await updateItem(storeName, dataToStore)

  // Prepare sync data
  let syncData = item
  if (isConnectorExecute) {
    if (item.body?.data && typeof item.body.data !== 'string') {
      syncData = {
        ...item,
        body: {
          ...item.body,
          data: JSON.stringify(item.body.data)
        }
      }
    }

    if (item[keyPath] && item.body && !item.body.recordId) {
      syncData = {
        ...syncData,
        body: {
          ...syncData.body,
          recordId: item[keyPath]
        }
      }
    }
  }

  // Add to sync queue
  await addToSyncQueue({
    type: 'update',
    storeName,
    data: syncData
  })

  // Try immediate sync if online
  if (isOnline) {
    processSyncQueue().catch(() => {})
  }

  return updatedItem
}

/**
 * Deletes an item with sync support
 * @param {string} storeName - The name of the store
 * @param {string|number} id - The ID of the item
 * @param {Object} [options] - Additional options
 * @param {RequestConfig} [options.requestConfig] - Additional request configuration
 * @returns {Promise<void>}
 */
export const deleteItemWithSync = async (storeName, id, options = {}) => {
  const isOnline = store.getState().core.online
  const keyPath = getStoreKeyPath(storeName)
  const { requestConfig = {} } = options
  const isConnectorExecute = requestConfig.headers?.['profile-key'] && requestConfig.headers?.['route-key']

  // Check item exists
  const item = await getItemById(storeName, id)
  if (!item) {
    throw new Error(`Item with ID ${id} not found in ${storeName}`)
  }

  // Delete locally
  await deleteItem(storeName, id)

  // Prepare sync data
  const syncData = isConnectorExecute
    ? {
        _id: id,
        body: {
          recordId: id,
          data: JSON.stringify({ deleted: true })
        },
        requestConfig
      }
    : { [keyPath]: id }

  // Add to sync queue
  await addToSyncQueue({
    type: 'delete',
    storeName,
    data: syncData
  })

  // Try immediate sync if online
  if (isOnline) {
    processSyncQueue().catch(() => {})
  }
}

/**
 * Initializes the sync system
 * This should be called after the Redux store is initialized
 */
export const initSync = () => {
  let lastOnlineState = store.getState().core.online

  // Listen for online events
  window.addEventListener('online', () => {
    processSyncQueue().catch(() => {})
  })

  // Listen for Redux store changes
  store.subscribe(() => {
    const currentOnlineState = store.getState().core.online
    if (currentOnlineState && !lastOnlineState) {
      processSyncQueue().catch(() => {})
    }
    lastOnlineState = currentOnlineState
  })

  // Check for pending items
  if (store.getState().core.online) {
    processSyncQueue().catch(() => {})
  }
}

/**
 * Manually triggers sync
 * @returns {Promise<boolean>} Success status
 */
export const triggerSync = () => {
  if (store.getState().core.online) {
    return processSyncQueue()
  }
  return Promise.resolve(false)
}
